import { DISABLE_PROCESSING } from '@/config'
import {
  type AccountSummary,
  PLAN_KINDS,
  type PlanKind,
  useAccount,
  useSummary,
  useUsage,
} from '@/modules/account'
import { isStatementToStart } from '@/modules/projects/status'
import { useProject, useStatements } from '../../hooks'
import type { Statement } from '../../stores'

const sumProcessablePages = (statements: Statement[]) =>
  statements
    .filter(isStatementToStart)
    .reduce((pages, statement) => pages + statement.numberOfPages, 0)

const hasStartableStatements = (statements: Statement[]) =>
  statements.some(isStatementToStart)

const hasUnstartableSelection = (selection: Statement[]) =>
  selection.some((statement) => !isStatementToStart(statement))

const hasEnoughCredits = (credits: number, pagesToProcess: number) =>
  credits > 0 && credits > pagesToProcess

const isMonthlyPageLimitReached = (
  summary: AccountSummary,
  pagesToProcess: number,
) =>
  (summary.monthlyPagesProcessed ?? 0) + pagesToProcess >=
  (summary.pagesPerMonthLimit ?? 0)

const useIsProcessingBlocked = (statementsToProcess?: Statement[]) => {
  const account = useAccount()
  const summary = useSummary()
  const { hasLimitedPages } = useUsage()
  const project = useProject()
  const statements = useStatements()

  if (project?.unprocessedPagesExceedsMonthlyLimit) return true
  if (!summary) return true
  if (account?.isDemo) return false

  const pagesToProcess = sumProcessablePages(statementsToProcess ?? statements)

  switch (account?.planKind) {
    case 'legacy_subscription':
      return (
        hasLimitedPages && isMonthlyPageLimitReached(summary, pagesToProcess)
      )
    case 'subscription':
      return !hasEnoughCredits(summary.pagesCredits, pagesToProcess)
    case 'single_project':
      return (
        Boolean(project?.expired) ||
        !hasEnoughCredits(summary.pagesCredits, pagesToProcess)
      )
    // The server cuts a trial run down to the pages left, so only an empty budget blocks it.
    case 'trial':
      return summary.pagesCredits <= 0
    default:
      return false
  }
}

const useBlockedReason = (): string => {
  const account = useAccount()
  const project = useProject()

  if (project?.unprocessedPagesExceedsMonthlyLimit) {
    return 'These statements exceed the monthly page limit'
  }

  const defaultMessage = 'Processing is not available'
  const messages: Partial<Record<PlanKind, string>> = {
    [PLAN_KINDS.legacy_subscription]: 'Monthly page limit reached',
    [PLAN_KINDS.single_project]: project?.expired
      ? 'Project expired'
      : 'Not enough credits',
    [PLAN_KINDS.subscription]: 'Not enough credits',
    [PLAN_KINDS.trial]: 'Your trial pages are used up',
  }

  const message = account ? messages[account.planKind] : null
  return message || defaultMessage
}

export const useProcessButtonState = (
  statementsToProcess?: Statement[],
  disabled?: boolean,
) => {
  const project = useProject()
  const statements = useStatements()
  const isBlocked = useIsProcessingBlocked(statementsToProcess) as boolean
  const blockedReason = useBlockedReason()

  const disabledReason = findDisabledReason({
    selection: statementsToProcess ?? [],
    statements,
    isBlocked,
    blockedReason,
  })

  const isProcessingDisabled =
    !project || disabledReason !== null || Boolean(disabled)

  return { isBlocked, isProcessingDisabled, disabledReason }
}

interface DisabledReasonInput {
  selection: Statement[]
  statements: Statement[]
  isBlocked: boolean
  blockedReason: string
}

const findDisabledReason = ({
  selection,
  statements,
  isBlocked,
  blockedReason,
}: DisabledReasonInput): string | null => {
  if (DISABLE_PROCESSING) return 'Processing is temporarily disabled'
  if (hasUnstartableSelection(selection)) {
    return 'Some selected statements are already processed or processing'
  }
  if (!hasStartableStatements(statements)) return 'Nothing to process'
  if (isBlocked) return blockedReason
  return null
}
